import type { Data, Layout } from "plotly.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChartPauseButton } from "../components/ChartPauseButton";
import { PlotlyChart } from "../components/PlotlyChart";
import { useChartPause } from "../hooks/useChartPause";

type SpRow = { ts_ms: number; cp: string; amps: number; timeout_s: number | null; reason: string | null; state: string | null };
type SpWindow = { now: number; from: number; rows: SpRow[] };

const WINDOWS = [15, 30, 60] as const;
const POLL_MS = 5000;

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page disabled:cursor-not-allowed disabled:opacity-40 disabled:text-text-tertiary";
const btnOn = "rounded-md border border-accent bg-accent px-2 py-1 text-white";

/** Stable across windows: Plotly otherwise colors by trace order, which changes with the filter. */
function colorForCharger(cp: string): string {
  let h = 2166136261;
  for (let i = 0; i < cp.length; i++) {
    h ^= cp.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `hsl(${(h >>> 0) % 360}, 68%, 42%)`;
}

type Series = {
  cp: string;
  x: number[];
  y: number[];
  text: string[];
  changed: boolean;
  last: number;
  state: string | null;
};

function buildSeries(win: SpWindow): Series[] {
  const byCp = new Map<string, Series>();
  for (const row of win.rows) {
    let s = byCp.get(row.cp);
    if (!s) {
      s = { cp: row.cp, x: [], y: [], text: [], changed: false, last: row.amps, state: row.state };
      byCp.set(row.cp, s);
    }
    if (s.y.length && s.y[s.y.length - 1] !== row.amps) s.changed = true;
    s.x.push(Math.max(row.ts_ms, win.from));
    s.y.push(row.amps);
    s.text.push(`${row.cp}  ${row.amps} A  ${row.timeout_s ?? "—"} s  ${row.reason ?? ""}  state=${row.state ?? "—"}`);
    s.last = row.amps;
    s.state = row.state;
  }
  for (const s of byCp.values()) {
    s.x.push(win.now);
    s.y.push(s.last);
    s.text.push(`${s.cp}  ${s.last} A  now`);
  }
  return [...byCp.values()].sort((a, b) => a.cp.localeCompare(b.cp));
}

export function LbSetpointTrend() {
  const [minutes, setMinutes] = useState<(typeof WINDOWS)[number]>(15);
  const [onlyChanging, setOnlyChanging] = useState(true);
  const [win, setWin] = useState<SpWindow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { paused, togglePause } = useChartPause();
  const pausedRef = useRef(paused);
  const loadRef = useRef<() => void>(() => {});
  const resumeFetch = useRef(false);
  pausedRef.current = paused;

  useEffect(() => {
    let stop = false;
    async function load() {
      try {
        const res = await fetch(`/api/lb/setpoints?minutes=${minutes}`, { cache: "no-store" });
        const body = (await res.json()) as SpWindow & { error?: string };
        if (stop) return;
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setWin(body);
        setError(null);
      } catch (err) {
        if (!stop) setError(err instanceof Error ? err.message : String(err));
      }
    }
    loadRef.current = () => void load();
    void load();
    const id = setInterval(() => {
      if (pausedRef.current) return;
      void load();
    }, POLL_MS);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [minutes]);

  useEffect(() => {
    if (paused) {
      resumeFetch.current = true;
      return;
    }
    if (!resumeFetch.current) return;
    resumeFetch.current = false;
    loadRef.current();
  }, [paused]);

  const series = useMemo(() => (win ? buildSeries(win) : []), [win]);
  const changed = series.filter((s) => s.changed);
  const charging = series.filter((s) => s.state === "C");
  const picked = series.filter((s) => s.changed || s.state === "C");
  const shown = onlyChanging && picked.length ? picked : series;

  const data = useMemo<Data[]>(
    () =>
      shown.map((s) => ({
        x: s.x,
        y: s.y,
        text: s.text,
        name: s.state === "C" ? `${s.cp} (C)` : s.cp,
        type: "scatter",
        mode: "lines",
        line: {
          shape: "hv",
          width: s.changed ? 1.8 : 1.2,
          dash: s.changed ? "solid" : "dot",
          color: colorForCharger(s.cp),
        },
        opacity: s.changed || s.state === "C" ? 1 : 0.35,
        hovertemplate: "%{text}<extra></extra>",
      })),
    [shown],
  );

  const layout = useMemo<Partial<Layout>>(
    () => ({
      paper_bgcolor: "#ffffff",
      plot_bgcolor: "#ffffff",
      margin: { l: 40, r: 8, t: 8, b: 28 },
      font: { family: "Inter, sans-serif", size: 10, color: "#6c757d" },
      showlegend: shown.length > 0 && shown.length <= 25,
      legend: { font: { size: 9 }, orientation: "v", x: 1.01, y: 1 },
      hovermode: "closest",
      uirevision: `lb-sp-${minutes}-${onlyChanging}`,
      xaxis: {
        type: "date",
        gridcolor: "#e9ecef",
        linecolor: "#e9ecef",
        autorange: false,
        range: win ? [win.from, win.now] : undefined,
      },
      yaxis: { title: { text: "A" }, gridcolor: "#e9ecef", linecolor: "#e9ecef", rangemode: "tozero" },
    }),
    [shown.length, minutes, onlyChanging, win],
  );

  return (
    <div className="mt-3 min-w-0">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2" style={{ fontSize: "var(--font-micro)" }}>
        <p className="text-text-secondary">
          Setpoint trend
          <span className="ml-2 text-text-tertiary">
            {error
              ? error
              : win
                ? `${changed.length} of ${series.length} chargers changed in ${minutes} min · ${charging.length} in C now` +
                  (onlyChanging && !picked.length && series.length ? " · none changed or in C, showing all" : "") +
                  ` · sent amps · ${paused ? "paused" : `every ${POLL_MS / 1000} s`}`
                : "loading…"}
          </span>
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={onlyChanging ? btnOn : btn}
            aria-pressed={onlyChanging}
            title="Show chargers whose sent setpoint changed in the window, plus chargers in state C at their last send"
            onClick={() => setOnlyChanging((on) => !on)}
          >
            Changing + C
          </button>
          {WINDOWS.map((m) => (
            <button
              key={m}
              type="button"
              className={minutes === m ? btnOn : btn}
              aria-pressed={minutes === m}
              onClick={() => setMinutes(m)}
            >
              {m} min
            </button>
          ))}
          <ChartPauseButton paused={paused} onToggle={togglePause} />
        </div>
      </div>
      <div className="h-72 rounded-md border border-border-subtle">
        {shown.length ? (
          <PlotlyChart data={data} layout={layout} revision={`${win?.now ?? 0}-${shown.length}-${onlyChanging}`} />
        ) : (
          <p className="p-3 text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
            No setpoints sent in this window.
          </p>
        )}
      </div>
    </div>
  );
}
